<template>
  <div v-show="bannerVisible" class="vf-cookie-consent">
    <section
      ref="panel"
      class="vf-cookie-consent__panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="vf-cookie-consent-title"
      aria-describedby="vf-cookie-consent-description"
      tabindex="-1"
      @keydown.esc="closeSettings"
    >
      <div class="vf-cookie-consent__copy">
        <p id="vf-cookie-consent-title" class="vf-cookie-consent__title">
          Your privacy choices
        </p>
        <p id="vf-cookie-consent-description">
          We use optional cookies and similar technologies to measure store
          performance and personalize advertising. Essential technologies for
          security, cart, and checkout always stay on.
          <a href="/cookies_policy">Cookie notice</a>
          <span aria-hidden="true"> · </span>
          <a href="/privacy_policy">Privacy policy</a>
        </p>
        <p v-if="gpcEnabled" class="vf-cookie-consent__gpc" role="status">
          Your browser's Global Privacy Control signal is active. Advertising,
          sale, and sharing are opted out on this device.
        </p>

        <div v-if="customizing" class="vf-cookie-consent__choices">
          <label>
            <input type="checkbox" checked disabled />
            <span
              ><strong>Essential</strong> — security, cart, checkout, and
              consent preferences</span
            >
          </label>
          <label>
            <input v-model="analytics" type="checkbox" />
            <span
              ><strong>Analytics</strong> — store usage and ecommerce
              measurement</span
            >
          </label>
          <label>
            <input v-model="marketing" type="checkbox" :disabled="gpcEnabled" />
            <span
              ><strong>Advertising</strong> — personalized advertising,
              measurement, and marketing partners</span
            >
          </label>
        </div>
      </div>

      <div class="vf-cookie-consent__actions">
        <button type="button" class="secondary" @click="rejectAll">
          Reject non-essential
        </button>
        <button
          v-if="!customizing"
          type="button"
          class="secondary"
          @click="showChoices"
        >
          Customize
        </button>
        <button v-else type="button" class="secondary" @click="saveChoices">
          Save choices
        </button>
        <button type="button" class="primary" @click="acceptAll">
          {{ gpcEnabled ? "Accept allowed" : "Accept all" }}
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, onUnmounted, ref } from "vue";
import type {
  ConsentApi,
  ConsentChoice,
  ConsentMethod,
} from "~/core/privacy/consent";

// Visible remains the SSR-safe fallback. The pre-paint consent experience in
// index.html hides this markup for the US opt-out-default mode and for
// returning visitors, while the footer control can always reopen it.
const bannerVisible = ref(true);
const customizing = ref(false);
const analytics = ref(false);
const marketing = ref(false);
const gpcEnabled = ref(false);
const panel = ref<HTMLElement | null>(null);

const getConsentApi = () =>
  (window as typeof window & { vfConsent?: ConsentApi }).vfConsent;

const applyChoice = (choice: ConsentChoice, method: ConsentMethod) => {
  const result = getConsentApi()?.update(choice, method);
  bannerVisible.value = false;
  customizing.value = false;
  // A reload is the only reliable way to stop already-running third-party
  // code after consent is withdrawn. GTM restarts with the denied state.
  if (result?.reloadRequired) window.location.reload();
};

const acceptAll = () =>
  applyChoice({ analytics: true, marketing: !gpcEnabled.value }, "accept_all");
const rejectAll = () =>
  applyChoice({ analytics: false, marketing: false }, "reject_optional");
const saveChoices = () =>
  applyChoice(
    { analytics: analytics.value, marketing: marketing.value },
    "customize"
  );

const showChoices = () => {
  customizing.value = true;
  nextTick(() => panel.value?.focus());
};

const openSettings = () => {
  const api = getConsentApi();
  const saved = api?.read();
  analytics.value = Boolean(saved?.analytics);
  marketing.value = Boolean(saved?.marketing);
  gpcEnabled.value = Boolean(api?.isGpcEnabled());
  customizing.value = true;
  bannerVisible.value = true;
  nextTick(() => panel.value?.focus());
};

const closeSettings = () => {
  // The initial privacy choice must remain explicit. Escape closes only a
  // preference center reopened after a valid decision already exists.
  if (!getConsentApi()?.hasChoice()) return;
  bannerVisible.value = false;
  customizing.value = false;
};

onMounted(() => {
  const api = getConsentApi();
  const saved = api?.read();
  gpcEnabled.value = Boolean(api?.isGpcEnabled());
  analytics.value = Boolean(saved?.analytics);
  marketing.value = Boolean(saved?.marketing);
  bannerVisible.value = !api?.hasChoice();
  // Keep the head-level pre-paint guard until Vue has applied v-show. Removing
  // it one tick later prevents a flash for returning visitors while leaving
  // the persistent settings control free to reopen the panel.
  nextTick(() => {
    delete document.documentElement.dataset.vfConsentState;
  });
  window.addEventListener("vf:open-cookie-settings", openSettings);
});

onUnmounted(() => {
  window.removeEventListener("vf:open-cookie-settings", openSettings);
});
</script>

<style scoped>
.vf-cookie-consent {
  position: relative;
  z-index: 10000;
}
.vf-cookie-consent__panel {
  position: fixed;
  right: 20px;
  bottom: 20px;
  left: 20px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  max-width: 1120px;
  margin: 0 auto;
  padding: 18px 20px;
  color: #171717;
  background: #fff;
  border: 1px solid #d8d3ca;
  border-radius: 12px;
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.22);
}
.vf-cookie-consent__copy {
  max-width: 700px;
  font-size: 13px;
  line-height: 1.45;
}
.vf-cookie-consent__title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
}
.vf-cookie-consent__copy p {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
}
.vf-cookie-consent__copy a {
  color: inherit;
  text-decoration: underline;
}
.vf-cookie-consent__gpc {
  margin-top: 8px !important;
  padding-left: 10px;
  border-left: 3px solid #171717;
  font-weight: 500;
}
.vf-cookie-consent__choices {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}
.vf-cookie-consent__choices label {
  display: flex;
  align-items: flex-start;
  gap: 9px;
}
.vf-cookie-consent__choices input {
  width: 16px;
  height: 16px;
  margin-top: 3px;
}
.vf-cookie-consent__choices input:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
.vf-cookie-consent__actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}
.vf-cookie-consent button {
  min-height: 40px;
  padding: 8px 14px;
  border: 1px solid #171717;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.vf-cookie-consent button.primary {
  color: #fff;
  background: #171717;
}
.vf-cookie-consent button.secondary {
  color: #171717;
  background: #fff;
}
.vf-cookie-consent__panel:focus-visible,
.vf-cookie-consent button:focus-visible,
.vf-cookie-consent a:focus-visible,
.vf-cookie-consent input:focus-visible {
  outline: 2px solid #171717;
  outline-offset: 3px;
}
@media (max-width: 767px) {
  .vf-cookie-consent__panel {
    right: 8px;
    bottom: 8px;
    left: 8px;
    display: block;
    max-height: calc(100vh - 16px);
    overflow-y: auto;
    padding: 10px;
    border-radius: 10px;
  }
  .vf-cookie-consent__copy {
    font-size: 12px;
    line-height: 1.32;
  }
  .vf-cookie-consent__title {
    margin-bottom: 3px;
    font-size: 14px;
    line-height: 1.25;
  }
  .vf-cookie-consent__actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 5px;
    margin-top: 8px;
  }
  .vf-cookie-consent button {
    min-height: 36px;
    padding: 5px 3px;
    font-size: 10.5px;
    line-height: 1.1;
  }
  .vf-cookie-consent__choices {
    gap: 7px;
    font-size: 12px;
  }
}
</style>
